import React from 'react';
import { useHistory } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Image, Typography } from 'lib/components';
import MenuList from './MenuList';
import { useAppDispatch, actions, useSelectTopNavState, useSelectCurrentUser, useSelectAuthorization } from 'store';
import { SIDE_NAV_ITEMS, SIDE_NAV_EXTRA_ITEMS } from 'lib/util';
import classes from './index.module.scss';

/**
 * SideNavModal component
 * Renders a slide-in navigation menu for mobile devices
 * Includes search, navigation links, and user profile
 * Uses framer-motion for animation effects
 *
 * @returns {JSX.Element} - Rendered component
 */
const SideNavModal = () => {
	// Router history for navigation
	const { push } = useHistory();

	// Redux state and actions
	const dispatch = useAppDispatch();
	const me = useSelectCurrentUser();
	const { canCreateJob } = useSelectAuthorization();
	const { sidenavVisible, searchBarVisible } = useSelectTopNavState();

	/**
	 * Close the side navigation menu
	 * Dispatches action to update Redux state
	 */
	const closeMenu = () => {
		dispatch(actions.sidenavToggled(false));
	};

	return (
		<AnimatePresence>
			{/* Only render when sidenav is visible */}
			{sidenavVisible && (
				<motion.div
					// Initial state (hidden off-screen)
					initial={{
						opacity: 0,
						translateX: '-60%',
					}}
					// Animated state (visible)
					animate={{
						opacity: 1,
						translateX: '0%',
						transition: {
							duration: 0.5,
						},
					}}
					// Exit state (slide out)
					exit={{
						opacity: 0,
						translateX: '-60%',
					}}
					className={classes['sidenav-modal']}
				>
					<div className={classes['sidenav-container']}>
						{/* Search bar - only shown when searchBarVisible is true */}
						{searchBarVisible && (
							<div className={classes['search-toolbar']}>
								<input placeholder="Search..." />
								<Image src="icSearch" />
							</div>
						)}
						<div className={classes['menu-items']}>
							{/* Main menu section header */}
							<Typography
								variant="h6"
								style={{
									marginTop: '2rem',
									marginLeft: '0.5rem',
								}}
								color="darkgrey"
							>
								MENU
							</Typography>
							{/* Main navigation menu items */}
							<MenuList list={SIDE_NAV_ITEMS} />
							{/* Add New Job button - only shown if user has permission */}
							{canCreateJob && (
								<button className={classes['create-new-dispatch']} onClick={() => push('/jobs/create')}>
									<p>Add New Job</p>
									<div className={classes['plus-icon']}>+</div>
								</button>
							)}
							{/* Extra menu section header */}
							<Typography
								variant="h6"
								style={{
									marginTop: 'auto',
									marginLeft: '0.5rem',
								}}
								color="darkgrey"
							>
								EXTRA
							</Typography>
							{/* Extra navigation menu items */}
							<MenuList list={SIDE_NAV_EXTRA_ITEMS} />
							{/* User profile section */}
							<div className={classes['admin-profile']}>
								{/* User avatar */}
								<Image
									src={me.avatar ?? 'plAvatar'} // Use placeholder avatar if user has none
									style={{
										width: '4.4rem',
										height: '4.4rem',
										borderRadius: '50%',
										marginRight: '1rem',
									}}
								/>
								<div>
									{/* User name */}
									<Typography variant="h4">{me.name}</Typography>
									{/* User role */}
									<Typography
										variant="h4"
										weight="light"
										color="darkgrey"
										style={{ marginTop: '0.5rem' }}
									>
										{me.roleId === 1 ? 'Super Administrator' : 'Administrator'}
									</Typography>
								</div>
							</div>
						</div>
					</div>
					{/* Close button */}
					<button className={classes['close-button']} onClick={closeMenu}>
						<Image src="icCancelBlack" />
					</button>
				</motion.div>
			)}
		</AnimatePresence>
	);
};

export default SideNavModal;
